// src/products/capRaise/components/TermsheetGenerator.js
// Updated termsheet generator with professional layout and governance integration

import React, { useState } from 'react';
import { FileText, Download, Eye, Printer } from 'lucide-react';
// import CurrencyService from '../../../services/currencyService'; // Unused
import { useMagnitude } from '../../../components/ui/MagnitudeSelector';

const TermsheetGenerator = ({ roundData, company }) => {
  const [previewMode, setPreviewMode] = useState(true);
  const { formatCurrencyWithMagnitude } = useMagnitude('millions');

  // Format currency using magnitude units
  const formatCurrency = (amount, currency = 'USD') => {
    return formatCurrencyWithMagnitude(amount || 0);
  };

  // Calculate derived values
  const calculatePostMoney = () => {
    return (roundData.preMoneyValuation || 0) + (roundData.targetAmount || 0);
  };

  const calculatePricePerShare = () => {
    const preMoneyValuation = roundData.preMoneyValuation || 0;
    
    // Assume 10 million pre-money shares (this would ideally come from cap table)
    const preMoneyShares = 10000000;
    
    if (preMoneyValuation > 0) {
      return preMoneyValuation / preMoneyShares;
    }
    
    return 0;
  };

  // Calculate actual dilution based on investment
  const calculateActualDilution = () => {
    const targetAmount = roundData.targetAmount || 0;
    const preMoneyValuation = roundData.preMoneyValuation || 0;
    const postMoneyValuation = preMoneyValuation + targetAmount;
    
    if (postMoneyValuation === 0) return 0;
    return (targetAmount / postMoneyValuation) * 100;
  };

  // Format Use of Funds data
  const formatUseOfFunds = () => {
    if (!roundData.useOfFunds || !Array.isArray(roundData.useOfFunds) || roundData.useOfFunds.length === 0) {
      return roundData.description || 'Funds will be used for general corporate purposes, including product development, team expansion, and market growth initiatives.';
    }

    const totalAmount = roundData.targetAmount || 0;
    const useOfFundsList = roundData.useOfFunds.map(item => {
      const amount = totalAmount * (item.percentage / 100);
      return `<li><strong>${item.category}:</strong> ${formatCurrency(amount)} (${item.percentage}%)</li>`;
    }).join('');

    return `
      <p><strong>Proposed allocation of proceeds:</strong></p>
      <ul style="margin: 10px 0; padding-left: 20px;">
        ${useOfFundsList}
      </ul>
      <p style="margin-top: 15px;"><strong>Total:</strong> ${formatCurrency(totalAmount)}</p>
    `;
  };

  // Generate professional termsheet HTML
  const generateTermsheetHTML = () => {
    const date = new Date();
    const day = date.getDate();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const currentDate = `${day} ${month} ${year}`;

    const excludedCountriesList = Array.isArray(roundData.excludedCountries) 
      ? roundData.excludedCountries.join(', ')
      : '';

    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Term Sheet - ${company?.company_name || company?.name || 'Company'} ${roundData.roundName || 'Funding Round'}</title>
    <style>
        body {
            font-family: 'Times New Roman', Times, serif;
            line-height: 1.6;
            color: #333;
            max-width: 100%;
            margin: 0;
            padding: 0;
            background: white;
            overflow-x: hidden;
        }
        
        .header {
            text-align: center;
            border-bottom: 3px double #333;
            padding-bottom: 20px;
            margin-bottom: 30px;
        }
        
        .company-logo {
            font-size: 24px;
            font-weight: bold;
            color: #2563eb;
            margin-bottom: 10px;
        }
        
        .document-title {
            font-size: 28px;
            font-weight: bold;
            margin: 15px 0;
            color: #1f2937;
        }
        
        .document-subtitle {
            font-size: 16px;
            color: #6b7280;
            margin-bottom: 10px;
        }
        
        .document-date {
            font-size: 14px;
            color: #6b7280;
            font-style: italic;
        }
        
        .section {
            margin: 25px 0;
            padding: 15px 0;
        }
        
        .section-title {
            font-size: 18px;
            font-weight: bold;
            color: #1f2937;
            border-bottom: 1px solid #d1d5db;
            padding-bottom: 8px;
            margin-bottom: 15px;
            text-transform: uppercase;
            letter-spacing: 0.5px;
        }
        
        .term-row {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            padding: 8px 0;
            border-bottom: 1px dotted #e5e7eb;
        }
        
        .term-label {
            font-weight: 600;
            color: #374151;
            min-width: 40%;
            padding-right: 20px;
        }
        
        .term-value {
            color: #1f2937;
            text-align: right;
            flex: 1;
        }
        
        .summary-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 30px;
            margin: 20px 0;
        }
        
        .highlight-box {
            background: #f8fafc;
            border: 1px solid #e2e8f0;
            border-radius: 8px;
            padding: 20px;
            margin: 15px 0;
        }
        
        .highlight-value {
            font-size: 20px;
            font-weight: bold;
            color: #2563eb;
        }
        
        .governance-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin: 15px 0;
        }
        
        .rights-section {
            background: #fefefe;
            border-left: 4px solid #2563eb;
            padding: 15px;
            margin: 10px 0;
        }
        
        .rights-title {
            font-weight: bold;
            color: #1f2937;
            margin-bottom: 8px;
        }
        
        .rights-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        
        .rights-list li {
            padding: 4px 0;
            position: relative;
            padding-left: 20px;
        }
        
        .rights-list li:before {
            content: "✓";
            position: absolute;
            left: 0;
            color: #059669;
            font-weight: bold;
        }
        
        .conditions-text {
            background: #fffbeb;
            border: 1px solid #fbbf24;
            border-radius: 6px;
            padding: 15px;
            font-style: italic;
            color: #92400e;
        }
        
        .confidential-footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 2px solid #374151;
            text-align: center;
            font-size: 12px;
            color: #6b7280;
        }
        
        .confidential-notice {
            background: #fef2f2;
            border: 1px solid #fca5a5;
            border-radius: 6px;
            padding: 15px;
            margin: 20px 0;
            text-align: center;
        }
        
        .signature-section {
            margin-top: 30px;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 40px;
        }
        
        .signature-box {
            border-top: 1px solid #374151;
            padding-top: 10px;
            text-align: center;
        }
        
        .page-break {
            page-break-before: always;
        }
        
        @media print {
            body {
                font-size: 12px;
                line-height: 1.4;
            }
            .no-print {
                display: none;
            }
        }
        .termsheet-container {
            padding: 40px 20px;
            max-width: 100%;
        }
    </style>
</head>
<body>
    <div class="termsheet-container">
    <!-- Header -->
    <div class="header">
        <div class="company-logo">${company?.company_name || company?.name || 'Company Name'}</div>
        <h1 class="document-title">TERM SHEET</h1>
        <div class="document-subtitle">${roundData.roundName || 'Funding Round'} - ${(roundData.stage || 'seed').charAt(0).toUpperCase() + (roundData.stage || 'seed').slice(1)} Investment</div>
        <div class="document-date">Prepared on ${currentDate}</div>
    </div>

    <!-- Executive Summary -->
    <div class="section">
        <h2 class="section-title">Executive Summary</h2>
        <div class="highlight-box">
            <div class="summary-grid">
                <div>
                    <div class="term-label">Total Investment</div>
                    <div class="highlight-value">${formatCurrency(roundData.targetAmount, roundData.currency)}</div>
                </div>
                <div>
                    <div class="term-label">Pre-Money Valuation</div>
                    <div class="highlight-value">${formatCurrency(roundData.preMoneyValuation, roundData.currency)}</div>
                </div>
            </div>
        </div>
    </div>

    <!-- Investment Terms -->
    <div class="section">
        <h2 class="section-title">Investment Terms</h2>
        
        <div class="term-row">
            <div class="term-label">Company:</div>
            <div class="term-value">${company?.company_name || company?.name || 'Company Name'}</div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Round Type:</div>
            <div class="term-value">${(roundData.stage || 'seed').charAt(0).toUpperCase() + (roundData.stage || 'seed').slice(1)} Round</div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Investment Instrument:</div>
            <div class="term-value">${(roundData.fundingInstrument || 'preference').replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Amount to be Raised:</div>
            <div class="term-value"><strong>${formatCurrency(roundData.targetAmount, roundData.currency)}</strong></div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Pre-Money Valuation:</div>
            <div class="term-value"><strong>${formatCurrency(roundData.preMoneyValuation, roundData.currency)}</strong></div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Post-Money Valuation:</div>
            <div class="term-value"><strong>${formatCurrency(calculatePostMoney(), roundData.currency)}</strong></div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Price per Share:</div>
            <div class="term-value">${calculatePricePerShare().toFixed(2)}</div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Investor Ownership:</div>
            <div class="term-value">${calculateActualDilution().toFixed(2)}%</div>
        </div>
        
        <div class="term-row">
            <div class="term-label">Minimum Investment:</div>
            <div class="term-value">${formatCurrency(roundData.minInvestment, roundData.currency)}</div>
        </div>
        
        ${roundData.maxInvestment ? `
        <div class="term-row">
            <div class="term-label">Maximum Investment:</div>
            <div class="term-value">${formatCurrency(roundData.maxInvestment, roundData.currency)}</div>
        </div>
        ` : ''}
        
        ${roundData.leadInvestorAmount ? `
        <div class="term-row">
            <div class="term-label">Lead Investor Amount:</div>
            <div class="term-value"><strong>${formatCurrency(roundData.leadInvestorAmount, roundData.currency)}</strong></div>
        </div>
        ` : ''}
        
        ${roundData.leadInvestor ? `
        <div class="term-row">
            <div class="term-label">Lead Investor:</div>
            <div class="term-value"><strong>${roundData.leadInvestor}</strong></div>
        </div>
        ` : ''}
    </div>

    <!-- Governance & Rights -->
    <div class="section">
        <h2 class="section-title">Governance & Investor Rights</h2>
        
        <div class="governance-grid">
            <div class="rights-section">
                <div class="rights-title">Board Composition</div>
                <ul class="rights-list">
                    ${roundData.boardSeats > 0 ? `<li>${roundData.boardSeats} investor-designated board seat${roundData.boardSeats > 1 ? 's' : ''}</li>` : '<li>No specific board seats allocated</li>'}
                    ${roundData.includeBoardMeetingRights === true ? '<li>Standard board meeting and information rights</li>' : roundData.includeBoardMeetingRights === false ? '<li>No board meeting rights</li>' : '<li>Board meeting rights - open to discussion</li>'}
                    ${roundData.includeConsentRights === true ? '<li>Consent rights for key corporate decisions</li>' : roundData.includeConsentRights === false ? '<li>No consent rights for decisions</li>' : '<li>Consent rights - open to discussion</li>'}
                </ul>
            </div>
            
            <div class="rights-section">
                <div class="rights-title">Protective Rights</div>
                <ul class="rights-list">
                    ${roundData.includeLiquidationPreference === true ? '<li>Liquidation preference rights</li>' : roundData.includeLiquidationPreference === false ? '<li>No liquidation preference</li>' : '<li>Liquidation preference - open to discussion</li>'}
                    ${roundData.includeAntiDilution === true ? '<li>Anti-dilution protection (weighted average)</li>' : roundData.includeAntiDilution === false ? '<li>No anti-dilution protection</li>' : '<li>Anti-dilution protection - open to discussion</li>'}
                    ${roundData.includeParticipationRights === true ? '<li>Participation rights</li>' : roundData.includeParticipationRights === false ? '<li>No participation rights</li>' : '<li>Participation rights - open to discussion</li>'}
                    ${roundData.includeProRataRights === true ? '<li>Pro-rata participation rights in future rounds</li>' : roundData.includeProRataRights === false ? '<li>No pro-rata rights in future rounds</li>' : '<li>Pro-rata rights - open to discussion</li>'}
                </ul>
            </div>
            
            <div class="rights-section">
                <div class="rights-title">Transfer Rights</div>
                <ul class="rights-list">
                    ${roundData.includeDragAlong === true ? '<li>Drag-along rights</li>' : roundData.includeDragAlong === false ? '<li>No drag-along rights</li>' : '<li>Drag-along rights - open to discussion</li>'}
                    ${roundData.includeTagAlong === true ? '<li>Tag-along rights</li>' : roundData.includeTagAlong === false ? '<li>No tag-along rights</li>' : '<li>Tag-along rights - open to discussion</li>'}
                    ${roundData.includeRightOfFirstRefusal === true ? '<li>Right of first refusal on share transfers</li>' : roundData.includeRightOfFirstRefusal === false ? '<li>No right of first refusal</li>' : '<li>Right of first refusal - open to discussion</li>'}
                    ${roundData.includeCoSaleRights === true ? '<li>Co-sale rights</li>' : roundData.includeCoSaleRights === false ? '<li>No co-sale rights</li>' : '<li>Co-sale rights - open to discussion</li>'}
                </ul>
            </div>
            
            <div class="rights-section">
                <div class="rights-title">Information Rights</div>
                <ul class="rights-list">
                    ${roundData.includeInformationRights === true ? '<li>Monthly financial statements</li>' : roundData.includeInformationRights === false ? '<li>No monthly financial reporting</li>' : '<li>Monthly financials - open to discussion</li>'}
                    ${roundData.includeInformationRights === true ? '<li>Annual audited financials</li>' : roundData.includeInformationRights === false ? '<li>No annual audit sharing</li>' : '<li>Annual audits - open to discussion</li>'}
                    ${roundData.includeInformationRights === true ? '<li>Board meeting materials</li>' : roundData.includeInformationRights === false ? '<li>No board materials sharing</li>' : '<li>Board materials - open to discussion</li>'}
                    ${roundData.includeInformationRights === true ? '<li>Inspection rights</li>' : roundData.includeInformationRights === false ? '<li>No inspection rights</li>' : '<li>Inspection rights - open to discussion</li>'}
                </ul>
            </div>
        </div>
    </div>

    <!-- Use of Proceeds -->
    <div class="section">
        <h2 class="section-title">Use of Proceeds</h2>
        <div class="conditions-text">
            ${formatUseOfFunds()}
        </div>
    </div>

    <!-- Conditions Precedent -->
    <div class="section">
        <h2 class="section-title">Conditions Precedent</h2>
        <div class="conditions-text">
            <p><strong>Investment completion is subject to:</strong></p>
            <ul style="margin: 10px 0; padding-left: 20px;">
                <li>Completion of satisfactory legal, financial, and commercial due diligence</li>
                <li>Execution of definitive legal documentation satisfactory to investors</li>
                <li>Receipt of all necessary corporate approvals and consents</li>
                <li>Compliance with applicable securities laws and regulations</li>
                ${roundData.leadInvestorAmount ? '<li>Confirmation of lead investor participation</li>' : ''}
            </ul>
        </div>
    </div>

    <!-- Signature Section -->
    <div class="signature-section">
        <div class="signature-box">
            <div style="margin-bottom: 40px;"></div>
            <div style="font-weight: bold;">Company Representative</div>
            <div>_________________________</div>
        </div>
        
        <div class="signature-box">
            <div style="margin-bottom: 40px;"></div>
            <div style="font-weight: bold;">Date</div>
            <div>_________________________</div>
        </div>
    </div>

    <!-- Investment Restrictions and Investor Qualifications -->
    <div class="section">
        <h2 class="section-title">Investment Restrictions & Investor Qualifications</h2>
        
        <!-- Confidential and Non-Binding Disclaimer -->
        <div class="confidential-notice">
            <strong>CONFIDENTIAL AND NON-BINDING TERM SHEET</strong><br>
            This document contains confidential and proprietary information. It is intended solely for the use of the recipient and may not be reproduced, distributed, or disclosed to any third party without the express written consent of ${company?.company_name || company?.name || 'the Company'}. This term sheet is for discussion purposes only and does not constitute a legally binding agreement. Any investment is subject to the execution of definitive legal documents and completion of satisfactory due diligence.
        </div>
        
        <!-- Wholesale/Sophisticated Investor Notice -->
        <div class="conditions-text">
            <p><strong>WHOLESALE/SOPHISTICATED INVESTOR NOTICE:</strong></p>
            <p>This investment opportunity is available only to wholesale investors (as defined in the Corporations Act 2001) or sophisticated investors who meet the criteria set out in section 708(8) of the Corporations Act 2001. By participating in this investment, you represent and warrant that you are a wholesale investor or sophisticated investor and acknowledge that you understand the risks associated with this investment.</p>
            <p>This investment is not suitable for retail investors and has not been prepared with retail investors in mind. No cooling-off rights apply to this investment.</p>
        </div>
        
        ${excludedCountriesList ? `
        <div style="margin-top: 20px;">
            <div class="term-row">
                <div class="term-label">Excluded Jurisdictions:</div>
                <div class="term-value">${excludedCountriesList}</div>
            </div>
        </div>
        ` : ''}
    </div>

    <!-- Footer -->
    <div class="confidential-footer">
        <p><strong>Generated by Kapitalized CapRaise Platform</strong></p>
        <p>This document was generated on ${currentDate} and contains confidential information.</p>
        <p>Any reproduction or distribution without written consent is prohibited.</p>
    </div>
    </div>
</body>
</html>`;
  };

  const handleDownloadPDF = () => {
    const htmlContent = generateTermsheetHTML();
    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    
    // Trigger print dialog for PDF generation
    setTimeout(() => {
      newWindow.print();
    }, 500);
  };

  const handlePrintTermsheet = () => {
    const htmlContent = generateTermsheetHTML();
    const newWindow = window.open('', '_blank');
    newWindow.document.write(htmlContent);
    newWindow.document.close();
    
    setTimeout(() => {
      newWindow.print();
    }, 500);
  };

  // Render the preview or edit mode
  if (!previewMode) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Termsheet Configuration</h2>
          <button
            onClick={() => setPreviewMode(true)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <Eye className="w-4 h-4 mr-2" />
            Preview
          </button>
        </div>
        
        <div className="bg-yellow-50 p-4 rounded-md">
          <p className="text-sm text-yellow-800">
            Termsheet configuration is automatically generated based on your round inputs. 
            Use the governance tab to modify investor rights and preferences.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-none space-y-6">
      {/* Toolbar */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <h3 className="text-lg font-semibold text-gray-900">Termsheet Actions</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <Eye className="w-4 h-4" />
              <span>Preview Mode</span>
            </div>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setPreviewMode(false)}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FileText className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={handlePrintTermsheet}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </button>
            <button
              onClick={handleDownloadPDF}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </button>
          </div>
        </div>
      </div>

      {/* Termsheet Preview */}
      <div className="w-full bg-white shadow-lg rounded-lg overflow-hidden">
        <div 
          className="w-full p-8"
          dangerouslySetInnerHTML={{ __html: generateTermsheetHTML() }}
        />
      </div>
    </div>
  );
};

export default TermsheetGenerator;